import { extendTheme } from "@chakra-ui/react";
import { MultiSelectTheme } from "chakra-multiselect";
import { borderTopLeftRadius } from "html2canvas/dist/types/css/property-descriptors/border-radius";

export const canaryYellow = "#FFF000";
export const deepBlueSky = "#00ACED";
export const newMoon = "#231F20";
export const vanillaGray = "#EFEFEA";

export const canaryYellowPalette = {
  50: "#FFFFE5",
  100: "#FFFFCC",
  200: "#FFFF99",
  300: "#FFFF66",
  400: "#FFFF33",
  500: "#FFFF00",
  600: "#FFEB00",
  700: "#FFD700",
  800: "#FFC300",
  900: canaryYellow,
};

export const gradientBg =
  "linear(to-r, indigo.400 20%, indigo.500 50%, indigo.700)";
export const gradientBgHover =
  "linear(to-r, indigo.500 20%, indigo.500 50%, indigo.700)";

export const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const colors = {
  brand: {
    100: vanillaGray,
    200: canaryYellowPalette[200],
    300: canaryYellowPalette[300],
    400: canaryYellowPalette[400],
    500: canaryYellowPalette[500],
    600: canaryYellowPalette[600],
    700: canaryYellowPalette[700],
    800: canaryYellowPalette[800],
    900: canaryYellowPalette[900],
    1000: newMoon,
    /* 100: "#E7DFD9",
    200: "#D0D0D0",
    400: "#84898C",
    700: "#A5B8D0",
    850: "#004e95",
    900: "#004584",
    1000: "#142D36", */
  },
  canary: canaryYellowPalette,
  primary: {
    25: "#e6eef4",
    100: "#dde4f1",
    200: "#b0bfdf",
    400: "#8EA4D2",
    700: "#6279B8",
    800: "#5c678e",
    900: "#49516F",
  },
  neutral: {
    0: "#FFFFFF",
    10: "#FCFCFC",
    25: "#F9F9F9",
    50: "#F5F5F5", //F2F0ED, EDEDED
    100: "#E8E9EB",
    200: "#CCCDC6",
    300: "#999999",
    400: "#777777",
    500: "#555555",
    600: "#525150",
    700: "#333333",
    800: "#202020",
    900: "#111111",
  },
  success: {
    25: "#DFF1EC",
    50: "#C9ECE1",
    100: "#B0E5D5",
    200: "#9EE1CC",
    300: "#87DCC1",
    400: "#6BD5B3",
    500: "#48CDA2",
    600: "#1CC38D",
    700: "#31A881",
    800: "#38846B",
    900: "#2E5045",
  },
  warning: {
    25: "#FBF8ED",
    50: "#FFF3D1",
    100: "#FCE6A8",
    200: "#FEDF86",
    300: "#FED456",
    400: "#FDC935",
    500: "#FDC023",
    600: "#FCB21E",
    700: "#FBA01C",
    800: "#FA8F1A",
    900: "#9A460F",
  },
  danger: {
    25: "#FFF3F7",
    50: "#FFE5ED",
    100: "#FEBDD3",
    200: "#FE92B5",
    300: "#FE6597",
    400: "#FD427F",
    500: "#FC1E68",
    600: "#EA1A65",
    700: "#D41661",
    800: "#BF105D",
    900: "#9A0557",
  },
  accent: {
    25: "#F5FAFF",
    50: "#E2F1FF",
    100: "#B9DCFF",
    200: "#88C6FF",
    300: "#53B8FD",
    400: "#0D9DFF",
    500: "#008BFF",
    600: "#007CFF",
    700: "#1E68FC",
    800: "#2955E9",
    900: "#332DC9",
  },
  indigo: {
    50: "#f0f0ff",
    100: "#d4d2ff",
    200: "#b3aaf8",
    400: "#9683ec",
    500: "#8a63d2",
    700: "#7e21d4",
  },
  black: {
    50: vanillaGray,
    900: newMoon,
  },
};

export const theme = extendTheme({
  colors,
  components: {
    Textarea: {
      variants: {
        outline: {
          rounded: "xl",
          bgColor: "white",
        },
      },
    },
    Input: {
      variants: {
        outline: {
          field: {
            rounded: "full",
            bgColor: "white",
          },
        },
      },
    },
    Select: {
      variants: {
        outline: {
          field: {
            rounded: "full",
            bgColor: "white",
          },
        },
      },
    },
    Button: {
      baseStyle: {
        fontWeight: 900,
        fontSize: 22,
        rounded: "full",
        fontFamily: `'Gotham', sans-serif`,
      },
      variants: {
        roleToggle: {
          transition: "all 200ms ease-in-out",
          bgColor: "brand.100",
          lineHeight: "100%",
          border: "1px solid",
          rounded: "full",
          borderColor: "brand.1000",
          color: "brand.1000",
          fontWeight: 500,
          py: 2,
          _active: {
            bgColor: "brand.900",
          },
          _hover: {
            bgColor: "brand.900",
          },
        },
        gradient: {
          bg: "primary.700",
          color: "white",
          transition: "all 0.2s ease-in-out",
          bgGradient: gradientBg,
          lineHeight: "100%",
          _hover: {
            boxShadow: "lg",
            bgGradient: gradientBgHover,
          },
        },
        ghost: {
          bgColor: "primary.100",
          lineHeight: "100%",
        },
        outline: {
          bgColor: "neutral.25",
          borderColor: "black.900",
          lineHeight: "100%",
          _hover: {
            bgColor: "black.900",
            color: "brand.900",
          },
        },
        link: {
          color: "brand.800",
          lineHeight: "100%",
        },
        primary: {
          bg: "canary.900",
          color: "black.900",
          lineHeight: "100%",
          _hover: {
            bg: "black.900",
            color: "canary.900",
          },
        },
        secondary: {
          bg: "black.900",
          color: "canary.900",
          lineHeight: "100%",
          _hover: {
            bg: "brand.800",
            color: "black.900",
          },
        },
        transparent: {
          bg: "transparent",
          _hover: {
            bg: "brand.900",
          },
        },
      },
    },
    Tag: {
      baseStyle: {
        rounded: "full",
        fontFamily: `'Gotham', sans-serif`,
      },
    },
    Heading: {
      baseStyle: {
        fontWeight: 500,
        fontFamily: `'Gotham', sans-serif`,
      },
    },
    FormLabel: {
      baseStyle: {
        fontWeight: 500,
        color: "primary.900",
      },
    },
    Card: {
      baseStyle: {
        container: {
          rounded: "lg",
          boxShadow: "none",
          color: "brand.1000",
        },
      },
    },
    Switch: {
      variants: {
        monthToggler: {
          container: {
            borderColor: "brand.1000",
          },
          thumb: {
            bgColor: "brand.1000",
          },
          track: {
            borderColor: "brand.1000",
            border: "1px solid",
            bg: "brand.900",
            bgColor: "brand.900",
            _checked: {
              borderColor: "brand.1000",
              border: "1px solid",
            },
          },
        },
      },
      baseStyle: {
        borderColor: "brand.1000",
        track: {
          borderColor: "brand.1000",
          border: "1px solid",
          bgColor: "brand.100",
          _checked: {
            borderColor: "brand.1000",
            border: "1px solid",
            bgColor: "brand.900",
          },
        },
      },
    },
    Checkbox: {
      baseStyle: {
        icon: {
          bgColor: "brand.900",
        },
        control: {
          _checked: {
            borderColor: "brand.900",
            bgColor: "brand.900",
          },
        },
      },
      variants: {
        black: {
          icon: {
            bgColor: "black.900",
          },
          control: {
            p: 2,
            borderColor: "black.900",
            _checked: {
              borderColor: "black.900",
              bgColor: "black.900",
            },
          },
        },
      },
    },
    MultiSelect: MultiSelectTheme,
    Menu: {
      baseStyle: {
        list: {
          spacing: "0.5rem",
          padding: "0",
          margin: "0",
          listStyleType: "none",
          border: "none",
        },
        item: {
          padding: "0.5rem 1rem",
          margin: "0",
          borderRadius: "md",
          transition: "background-color 0.2s ease-in-out",
          _hover: {
            backgroundColor: "brand.900",
          },
          _active: {
            backgroundColor: "brand.900",
          },
          _focus: {
            backgroundColor: "brand.900",
          },
        },
        link: {
          display: "block",
          textDecoration: "none",
          color: "black.900",
          _hover: {
            textDecoration: "none",
          },
        },
      },
    },
  },
  styles: {
    global: {
      ".landing-page": {
        fontFamily: '"Gotham", sans-serif',
      },
      html: {
        fontSize: "16px",
      },
      ".roleToggle": {
        viewTransitionName: "roleToggle",
      },
      "html, body": {
        bg: "black.50",
        fontFamily: `'Gotham', sans-serif`,
        color: "black.900",
      },
      ".CookieConsent a, .CookieConsent a:hover, .CookieConsent a:visited, .CookieConsent a:active":
        {
          textDecoration: "none",
          fontWeight: 900,
          color: "black",
        },
      ".ant-picker-cell-disabled": {
        backgroundColor: colors.neutral[25],
      },
      ".text-canary-900": {
        color: colors.canary[900],
      },
      ".ql-container.ql-snow": {
        backgroundColor: "white",
        border: "none",
        borderRadius: "12px",
      },
      ".quill": {
        border: "1px solid",
        borderColor: "brand.100",
        borderRadius: "12px",
      },
      ".ql-toolbar.ql-snow": {
        border: "none",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        borderBottom: "1px solid",
        borderColor: "brand.100",
      },
      ".course-content": {
        color: "black.900",
        fontWeight: 400,
        h1: {
          fontSize: "2rem",
          my: 2,
        },
        h2: {
          fontSize: "1.5rem",
          my: 2,
        },
        h3: {
          fontSize: "1.25rem",
          my: 2,
        },
        p: {
          mb: 2,
        },
        figure: {
          my: 2,
          margin: "0 auto",
          display: "flex",
          justifyContent: "center",
          img: {
            my: 0,
          },
        },
        img: {
          my: 2,
          margin: "0 auto",
        },
      },
    },
  },
});
